import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {NestedSheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NestedSheet';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {SheetSlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSlotLocation';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {
    DEFAULT_NUM_COLS,
    DEFAULT_NUM_ROWS
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotConstants';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {
    FrameDetailsEntry,
    FrameDetailsEntryKey
} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {IValue} from '@mintdata-wsp/mint-kit/api/types/Value';
import {AError} from '@mintdata-wsp/mint-kit/api/values/AError';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {FrameCellCoordinate, FramePath, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {action, IReactionDisposer, reaction, runInAction} from 'mobx';

export class FrameDetailsController
{
    private _fixedRowReactionDisposer:IReactionDisposer = null;
    private _onSlotDataChangedDisposers:Map<SheetId, IReactionDisposer> = new Map();

    private _onSlotDataChanged(sheet:AnySheet, slotLocation:SlotLocation):IReactionDisposer
    {
        const sheetCtrl = commonControllers.spreadsheets.workbook.getSheetCtrl(sheet.id);

        return reaction(
            () =>
            {
                const slot = sheetCtrl.findSlot(sheet.id, slotLocation);

                return slot && slot.value;
            },
            () =>
            {
                const slot = sheetCtrl.findSlot(sheet.id, slotLocation);
                const highestEntry = this.getHighestEntry(sheet.workbook.id, sheet.id);

                if( !slot || !slot.containsFrame )
                {
                    if( highestEntry )
                        this.exitNestedView(sheet.workbook.id, sheet.id, sheet.id);
                }
                else
                {
                    if( highestEntry )
                    {
                        this.exitNestedView(sheet.workbook.id, sheet.id, highestEntry.sheet.id);

                        if( slot.containsFrame )
                            this.enterNestedView(sheet.workbook.id, sheet.id, sheet.id, highestEntry.mainSheetSlotLocation);
                    }
                }
            });
    }

    public init()
    {
        reaction(
            () =>
            {
                const {ui: {spreadsheets: {main}, openDesign}} = controllers;
                const openedDesign = openDesign.getOpenDesign();

                if( openedDesign )
                {
                    return main.getAllSheets().map((sheet:Sheet) => (
                        this.getStack(sheet.workbook.id, sheet.id).length
                    ));
                }
            },
            () =>
            {
                const {ui: {spreadsheets: {main, workbook}, openDesign}} = controllers;
                const openedDesign = openDesign.getOpenDesign();

                this._onSlotDataChangedDisposers.forEach((dispose) => dispose());
                this._onSlotDataChangedDisposers.clear();

                if( !openedDesign )
                    return;

                const allSheets:AnySheet[] = main.getAllSheets();

                allSheets.forEach((sheet:AnySheet) =>
                {
                    const sheetCtrl = workbook.getSheetCtrl(sheet.id);
                    const highestEntry = this.getHighestEntry(sheet.workbook.id, sheet.id);

                    if( highestEntry )
                    {
                        const slot = sheetCtrl.findSlot(sheet.id, highestEntry.mainSheetSlotLocation);

                        if( slot )
                        {
                            let disposer = this._onSlotDataChangedDisposers.get(sheet.id);

                            if( !disposer )
                            {
                                disposer = this._onSlotDataChanged(sheet, highestEntry.mainSheetSlotLocation);
                                this._onSlotDataChangedDisposers.set(sheet.id, disposer);
                            }
                        }
                    }
                    else
                    {
                        const dispose = this._onSlotDataChangedDisposers.get(sheet.id);

                        if( dispose )
                        {
                            dispose();
                            this._onSlotDataChangedDisposers.delete(sheet.id);
                        }
                    }
                });
            });
    }

    public getStack(workbookId:WorkbookId, mainSheetId:SheetId):FrameDetailsEntry[]
    {
        const {ui: {spreadsheets: {tabs}}} = controllers;
        const perTabState = tabs.getOrCreateState(workbookId, mainSheetId);

        return perTabState.frameDetailsStack;
    }

    public getEntryByKey(workbookId:WorkbookId, mainSheetId:SheetId, key:FrameDetailsEntryKey):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);

        return stack.find((entry:FrameDetailsEntry) => entry.key === key) || null;
    }

    public getEntryBySheetId(workbookId:WorkbookId, mainSheetId:SheetId, sheetId:SheetId):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);

        return stack.find(({sheet}) => sheet.id === sheetId) || null;
    }

    public hasEntry(workbookId:WorkbookId, mainSheetId:SheetId, key:FrameDetailsEntryKey):boolean
    {
        const stack = this.getStack(workbookId, mainSheetId);

        return !!stack.find((entry:FrameDetailsEntry) => entry.key === key);
    }

    public getHighestEntry(workbookId:WorkbookId, mainSheetId:SheetId):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);

        return stack.length > 0 ? stack[0] : null;
    }

    public getDeepestEntry(workbookId:WorkbookId, mainSheetId:SheetId):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);

        if( stack.length > 0 )
        {
            return stack[stack.length - 1];
        }

        return null;
    }

    public getPreviousEntry(workbookId:WorkbookId, mainSheetId:SheetId, key:FrameDetailsEntryKey):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);
        const index = stack.findIndex((entry:FrameDetailsEntry) => entry.key === key);
        const prevIndex = index - 1;

        return prevIndex > -1 ? stack[prevIndex] : null;
    }

    public getEnteredEntries(workbookId:WorkbookId, mainSheetId:SheetId):FrameDetailsEntry[]
    {
        const stack = this.getStack(workbookId, mainSheetId);
        const result = [...stack];

        result.pop();

        return result;
    }

    public getEntryFrameLocation(entry:FrameDetailsEntry):SlotLocation
    {
        const {length} = entry.pathInFrame;
        let {colIndex, rowIndex} = entry.mainSheetSlotLocation;

        if( length )
        {
            const previousEntry = this.getPreviousEntry(entry.workbookId, entry.mainSheetId, entry.key);
            const fieldName = entry.pathInFrame[length - 1].fieldName;

            rowIndex = entry.pathInFrame[length - 1].rowIndex;
            colIndex = previousEntry.sheet.columnNames.indexOf(fieldName);
        }

        return new SlotLocation(colIndex, rowIndex);
    }

    public isEntered(entry:FrameDetailsEntry):boolean
    {
        const stack = this.getStack(entry.workbookId, entry.mainSheetId);
        const index = stack.indexOf(entry);

        return stack.length > 1 && index !== stack.length - 1;
    }

    public getNestedViewTitle(workbookId:WorkbookId, mainSheetId:SheetId):string
    {
        const highestEntry = this.getHighestEntry(workbookId, mainSheetId);
        return highestEntry ? highestEntry.sheet.name : '';

    }

    @action
    public pushEntry(workbookId:WorkbookId, mainSheetId:SheetId, entry:FrameDetailsEntry):boolean
    {
        if( !this.hasEntry(workbookId, mainSheetId, entry.key) )
        {
            const stack = this.getStack(workbookId, mainSheetId);
            stack.push(entry);

            return true;
        }

        return false;
    }

    @action
    public popDeepestEntry(workbookId:WorkbookId, mainSheetId:SheetId):FrameDetailsEntry | null
    {
        const stack = this.getStack(workbookId, mainSheetId);
        let entry:FrameDetailsEntry = null;

        if( stack.length > 0 )
            entry = stack.pop();

        this._focusLastStackEntrySheet(stack, entry && entry.mainSheetId);

        return entry;
    }

    private _focusLastStackEntrySheet(stack:Array<FrameDetailsEntry>, mainSheetId:SheetId):void
    {
        const {ui: {spreadsheets: {mesh}}} = controllers;
        const sheetId:SheetId = stack.length > 0
            ? stack[stack.length - 1].sheet.id
            : mainSheetId;

        if( sheetId )
        {
            const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId);

            mesh.focusMesh(ctx);
        }
    }

    @action
    public replaceEntry(oldEntry:FrameDetailsEntry, newEntry:FrameDetailsEntry)
    {
        if( oldEntry.workbookId != newEntry.workbookId || oldEntry.mainSheetId != newEntry.mainSheetId )
        {
            console.error(`args are invalid for replaceEntry.`);
            return;
        }

        const stack = this.getStack(oldEntry.workbookId, oldEntry.mainSheetId);
        if( stack.length > 0 )
        {
            const oldEntryIndex = stack.findIndex(item => item.key == oldEntry.key);
            if( oldEntryIndex >= 0 )
                stack.splice(oldEntryIndex, 1, newEntry);
        }
    }

    @action
    public popAllAfterEntry(workbookId:WorkbookId, mainSheetId:SheetId, key:FrameDetailsEntryKey):FrameDetailsEntry[]
    {
        const stack = this.getStack(workbookId, mainSheetId);

        const index = stack.findIndex((entry:FrameDetailsEntry) => entry.key === key);
        const deletedEntries:Array<FrameDetailsEntry> = stack.splice(index + 1);

        this._focusLastStackEntrySheet(stack, mainSheetId);

        return deletedEntries;
    }

    @action
    public exitNestedView(workbookId:WorkbookId, mainSheetId:SheetId, sheetId:SheetId)
    {
        const entry = this.getEntryBySheetId(workbookId, mainSheetId, sheetId);
        const key = entry ? entry.key : null;

        this.popAllAfterEntry(workbookId, mainSheetId, key);
    }

    @action
    public exitDeepestNestedView(workbookId:WorkbookId, mainSheetId:SheetId)
    {
        this.popDeepestEntry(workbookId, mainSheetId);
    }

    public async enterNestedViewByPath(workbookId:WorkbookId,
                                       mainSheetId:SheetId,
                                       slotLocation:SlotLocation,
                                       pathInFrame:FramePath):Promise<SheetSlotLocation | null>
    {
        const sheetCtrl = commonControllers.spreadsheets.workbook.getSheetCtrl(mainSheetId);

        const frameSlot = sheetCtrl.findSlot(mainSheetId, slotLocation);
        if( !frameSlot )
            return null;

        let currentEntry;
        let reuseAllowed = true;

        let stack = this.getStack(workbookId, mainSheetId);
        if( stack.length > 0 && stack[0].mainSheetSlotLocation.isEqualTo(frameSlot.location) )
        {
            currentEntry = stack[0];
        }
        else
        {
            reuseAllowed = false;
            currentEntry = await this.enterNestedView(
                workbookId,
                mainSheetId,
                mainSheetId,
                slotLocation
            );
            if( !currentEntry )
                return null;
        }

        // redundant case
        if( !pathInFrame || pathInFrame.length == 0 )
            return new SheetSlotLocation(currentEntry.sheet, slotLocation.colIndex, slotLocation.rowIndex);

        for( let i = 0; i < pathInFrame.length - 1; i++ )
        {
            if( reuseAllowed && i + 1 < stack.length )
            {
                let existingEntry = stack[i + 1];
                if( Frames.isStrictFramePathPrefix(existingEntry.pathInFrame, pathInFrame) )
                {
                    currentEntry = existingEntry;
                    continue;
                }
                reuseAllowed = false;
            }

            const {fieldName, rowIndex} = pathInFrame[i];
            const colIndex = currentEntry.sheet.columnNames.indexOf(fieldName);

            currentEntry = await this.enterNestedView(
                workbookId,
                mainSheetId,
                currentEntry.sheet.id,
                new SlotLocation(colIndex, rowIndex)
            );

            if( !currentEntry )
                return null;
        }

        // refresh stack - it might have changed
        stack = this.getStack(workbookId, mainSheetId);
        if( stack.length > pathInFrame.length )
        {
            // and close any extra open views
            const closeEntry = stack[pathInFrame.length];
            this.exitNestedView(workbookId, mainSheetId, closeEntry.key);
        }

        const {fieldName, rowIndex} = pathInFrame[pathInFrame.length - 1];
        return new SheetSlotLocation(
            currentEntry.sheet,
            currentEntry.sheet.columnNames.indexOf(fieldName),
            rowIndex
        );
    }

    public async enterNestedView(workbookId:WorkbookId,
                                 mainSheetId:SheetId,
                                 sheetId:SheetId,
                                 activeCell:SlotLocation):Promise<FrameDetailsEntry | null>
    {
        const sheetCtrl = commonControllers.spreadsheets.workbook.getSheetCtrl(sheetId);

        const {ui: {spreadsheets}} = controllers;

        const slotValue:IValue = sheetCtrl.getSlotValue(sheetId, activeCell);

        const isFrameable = slotValue.isFrame || slotValue.isError;

        if( !isFrameable )
            return null;

        const ctx:IWSPMeshContext = this._getMeshContextBySheetId(sheetId);
        spreadsheets.meshSelection.checkActiveCellCol(ctx, activeCell.colIndex);
        spreadsheets.meshSelection.checkActiveCellRow(ctx, activeCell.rowIndex);

        const workbook = spreadsheets.main.findWorkbookById(workbookId);

        let frame:IFrame;
        if( slotValue.isFrame )
            frame = slotValue as IFrame;
        else if( slotValue.isError )
            frame = Frames.forErrorWithContext(slotValue as AError, (slotValue as AError).errorContext);

        const key:FrameDetailsEntryKey = sheetId;

        const frameDetailsEntry = this.getEntryByKey(workbookId, mainSheetId, key);

        if( frameDetailsEntry )
            runInAction(() => this.exitNestedView(workbookId, mainSheetId, key));

        let mainSheetSlotLocation = activeCell;
        let pathInFrame = [];

        const deepestFrameDetailsEntry = this.getDeepestEntry(workbookId, mainSheetId);
        if( deepestFrameDetailsEntry )
        {
            mainSheetSlotLocation = deepestFrameDetailsEntry.mainSheetSlotLocation;

            pathInFrame = [
                ...deepestFrameDetailsEntry.pathInFrame,
                new FrameCellCoordinate(
                    deepestFrameDetailsEntry.sheet.columnNames[activeCell.colIndex],
                    activeCell.rowIndex
                )
            ];
        }

        const mainSheet = spreadsheets.workbook.findSheetById(workbookId, mainSheetId);
        const columnName = MeshLocationUtils.getLetterByColumnIndex(mainSheetSlotLocation.colIndex);

        const title = `'${mainSheet.name}'!${columnName}${mainSheetSlotLocation.rowIndex + 1}`;

        const loadedSheet:NestedSheet = spreadsheets.workbook.createNewSheetFromFrame(workbook, title, frame);

        const fields = loadedSheet.frame.getFields();

        const numCols:number = Math.max(fields.length, DEFAULT_NUM_COLS);
        const numRows:number = Math.max(loadedSheet.frame.getRowsCount(), DEFAULT_NUM_ROWS);
        const loadedMeshState:MeshState = MeshState.create(loadedSheet, numCols, numRows);

        // nested frame is readonly
        loadedMeshState.details.isReadonly = true;

        const loadedFrameEntry = new FrameDetailsEntry(
            key,
            workbookId,
            mainSheetId,
            mainSheetSlotLocation,
            pathInFrame,
            loadedSheet,
            loadedMeshState,
            false
        );

        return runInAction(() =>
        {
            this.pushEntry(workbookId, mainSheetId, loadedFrameEntry);
            spreadsheets.sheetSettings.createSheetSettings(loadedSheet.id);

            return loadedFrameEntry;
        });
    }

    public getEnteredFrameCell(sheetId:SheetId):MeshLocation | null
    {
        const {main, tabs} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetId, true) as Sheet;

        const activeSheet = tabs.getActiveSheet(sheet.workbook.id);
        const frameDetailsEntry = this.getEntryByKey(sheet.workbook.id, activeSheet.id, sheet.id);

        if( frameDetailsEntry )
        {
            const {colIndex, rowIndex} = this.getEntryFrameLocation(frameDetailsEntry);
            return new MeshLocation(colIndex, rowIndex);
        }

        return null;
    }

    public addFixedRowReaction(workbookId:WorkbookId, sheetId:SheetId):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const stack = this.getStack(workbookId, sheetId);

        this._fixedRowReactionDisposer = reaction(
            () => stack.length,
            () =>
            {
                const stack = this.getStack(workbookId, sheetId);
                const enteredFrameDetails = this.getEnteredEntries(workbookId, sheetId);

                stack.forEach((frameDetailsEntry) =>
                {
                    const ctx:IWSPMeshContext = this._getMeshContextBySheetId(frameDetailsEntry.sheet.id);
                    const meshState = mesh.getState(ctx);
                    meshState.details.fixedRowIndex = null;
                });

                enteredFrameDetails.forEach((frameDetailsEntry) =>
                {
                    const ctx:IWSPMeshContext = this._getMeshContextBySheetId(frameDetailsEntry.sheet.id);
                    const meshState = mesh.getState(ctx);
                    const enteredFrameCell = this.getEnteredFrameCell(frameDetailsEntry.sheet.id);

                    meshState.details.fixedRowIndex = enteredFrameCell.rowIndex;
                });
            });
    }

    public disposeFixedRowReaction():void
    {
        if( this._fixedRowReactionDisposer )
        {
            this._fixedRowReactionDisposer();
            this._fixedRowReactionDisposer = null;
        }
    }

    @action.bound
    public computeFramePathByContext(ctx:IWSPMeshContext, location:SlotLocation):{mainLocation:SlotLocation, framePath:FramePath}
    {
        const {workbookId, mainSheetId, sheetId} = ctx;

        const isTopLevelSheet = mainSheetId === sheetId;
        if( isTopLevelSheet )
            return {mainLocation: location, framePath: null};

        const frameEntry = this.getEntryBySheetId(workbookId, mainSheetId, sheetId);

        const {rowIndex, colIndex} = location;
        const fieldName = frameEntry.sheet.columnNames[colIndex];

        return {
            framePath: [
                ...frameEntry.pathInFrame,
                new FrameCellCoordinate(fieldName, rowIndex)
            ] as FramePath,
            mainLocation: frameEntry.mainSheetSlotLocation
        };
    }

    private _getMeshContextBySheetId(sheetId:SheetId):IWSPMeshContext
    {
        const {workbook} = controllers.ui.spreadsheets;

        return workbook.getMeshContextBySheetId(sheetId);
    }
}

